import { HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import {
  AlertController,
  LoadingController,
  ModalController,
} from "ionic-angular";
import moment from "moment";
import { Subscription } from "rxjs";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import { CBSAPayer } from "../../../../models/payers/payer";
import { stateList } from "../../../../pages/patient-chart/patient-benefits/models";
import { PayersService } from "../../../../providers/payers-service/payers.service";
import { NgbCustomParserFormatter } from "../../../common/date-picker/ngb-custom-parser-formatter";
import {
  ContractPeriod,
  Counties,
  HospiceLOCPayerRates,
  HospiceLOCRate,
  Options,
} from "../models";

@Component({
  selector: "hospice-loc",
  templateUrl: "./hospice-loc.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RatesHospiceLOC),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RatesHospiceLOC),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class RatesHospiceLOC implements OnInit, OnDestroy {
  hospiceLOCRatesFormGroup: FormGroup;
  cbsaRates: CBSAPayer[] = [];
  readOnlyCbsaRates: CBSAPayer[] = [];
  cbsaRatesPreviousSelected: CBSAPayer[] = [];
  cbsaRatesPreloaded: CBSAPayer[] = [];
  sortOptions: SortOptions = new SortOptions("cbsa", "asc");
  stateList = stateList;
  stateOptions: Options[];
  contractPeriodsOptions: ContractPeriod[];
  addNewContractPeriodOption: ContractPeriod;
  selectedStates: Options[];
  stateOptionsPreloaded: any[];
  countyOptions: Counties[];
  countyOptionsPreloaded: string[];
  selectorOpen: boolean = false;
  _saveEnabled: boolean = false;
  subscriptions: Subscription[] = [];
  hospiceLOCRatesData: HospiceLOCPayerRates;
  previousContractPeriodSelected: ContractPeriod;
  payerId: string;
  payerType: string;
  dateFormat: string = "YYYY-MM-DD";
  dateUIFormat: string = "MM/DD/YYYY";
  chunkCountiesSize: number = 10;
  periodSelected: boolean = false;
  benefitType: string = "HOSPICE";
  benefitSubtype: string = "LOC";
  readonly: boolean = false;
  editMode: boolean = false;
  rc60CopyOption: boolean = false;
  rc61CopyOption: boolean = false;
  continuousCareCopyOption: boolean = false;
  inpatientRCCopyOption: boolean = false;
  generalICopyOption: boolean = false;
  minDateFormat: any = moment("2023-09-30", this.dateFormat);

  @Output() onCanceled = new EventEmitter<any>();
  @Output() onHospiceLOCRateSaveSuccess = new EventEmitter<any>();
  @Output() onRatesSaved = new EventEmitter<any>();
  @Input() currentPayer: any;
  @Input() isEditMode: boolean = false;
  @Input() currentPayerType: any;

  constructor(
    private payersService: PayersService,
    protected formBuilder: FormBuilder,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private loadingCtrl: LoadingController
  ) {}

  cleanData(addNewPeriodContractAction: boolean = true): void {
    // States
    this.stateOptions = this.stateOptions.map((state) => {
      return { ...state, disabled: false };
    });
    this.selectedStates = [];
    this.stateOptionsPreloaded = [];

    // Counties
    this.hospiceLOCRatesFormGroup.get("selectedCounties").setValue([]);
    this.countyOptions = [];
    this.countyOptionsPreloaded = [];

    // CBSA
    this.cbsaRates.forEach((rate) => {
      this.removeCBSAFormControlRow(rate.cbsa);
    });
    this.cbsaRates = [];
    this.cbsaRatesPreviousSelected = [];
    this.cbsaRatesPreloaded = [];

    // Flags
    this.saveEnabled = false;
    this.periodSelected = false;
    this.hospiceLOCRatesFormGroup.get("selectedCounties").disable();
    if (!this.readonly && addNewPeriodContractAction) {
      this.addNewPeriodContractAction(true);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  ngOnInit(): void {
    this.setReadOnly();
    this.loadOptions();
    this.initFormGroup();
  }

  onPeriodChanged() {
    // remove controls
    this.cbsaRates.forEach((rate) => {
      this.removeCBSAFormControlRow(rate.cbsa);
    });
    // clean data
    this.setReadOnly();
    this.loadOptions();
    this.cleanData();
    this.hospiceLOCRatesFormGroup.reset();
    this.getPayerContractPeriods();
  }

  setReadOnly() {
    this.readonly =
      this.currentPayerType && this.currentPayerType.length > 0
        ? this.currentPayerType == "MEDICARE"
        : false;
  }

  initFormGroup() {
    this.hospiceLOCRatesFormGroup = this.formBuilder.group({});
    this.subscriptions.push(
      this.hospiceLOCRatesFormGroup.valueChanges.subscribe(this.onFormChanges)
    );
    this.hospiceLOCRatesFormGroup.addControl(
      "selectedContractPeriod",
      new FormControl(null, [
        Validators.required,
        this.notAddNewSelectedValidator(),
      ])
    );
    this.hospiceLOCRatesFormGroup.addControl(
      "selectedCounties",
      new FormControl(null, [Validators.required])
    );
    this.hospiceLOCRatesFormGroup.get("selectedCounties").disable();
  }

  notAddNewSelectedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid = control.value != 0;
      return isValid ? null : { notZero: { value: control.value } };
    };
  }

  onFormChanges = (value) => {
    if (value) {
      this.checkCanSave();
    }
  };

  loadOptions(): void {
    this.stateOptions = this.stateList.map((s) => {
      return {
        name: s,
        value: s,
        disabled: false,
      };
    });

    if (!this.readonly) {
      this.addNewContractPeriodOption = {
        id: 0,
        name: "Add New",
        value: "Add New",
        effectiveDate: "",
        expiredDate: "",
      };

      this.contractPeriodsOptions = [];
      this.contractPeriodsOptions.push(this.addNewContractPeriodOption);
    }

    if (
      this.isEditMode &&
      this.currentPayer &&
      this.currentPayer.payerId != ""
    ) {
      this.getPayerTypeAndId();
      this.getPayerContractPeriods();
    }
  }

  getPayerTypeAndId() {
    this.payerId = this.currentPayer.payerId;
    this.payerType = this.currentPayer.payerType;
  }

  getPayerContractPeriods() {
    let payerId = this.payerId;
    let benefitType = this.benefitType;
    let benefitSubtype = this.benefitSubtype;

    if (this.readonly) {
      payerId = "MEDICARE";
      benefitType = "HOSPICE";
      benefitSubtype = "LOC";
    }

    this.payersService
      .getPayerRatesPeriods(payerId, benefitType, benefitSubtype)
      .pipe()
      .take(1)
      .subscribe((response) => {
        let previousContractPeriods: ContractPeriod[] = [];
        if (response !== null) {
          let contractPeriod = Object.values(response);
          contractPeriod.forEach((period) => {
            let name = moment(period.start).format(this.dateUIFormat);
            let expiredDate = null;
            if (period.end && period.end != "") {
              expiredDate = moment(period.end).format(this.dateUIFormat);
              name =
                name + " - " + moment(period.end).format(this.dateUIFormat);
            }
            let newPeriod = {
              id: previousContractPeriods.length + 1,
              name: name,
              value: moment(period.start).format(this.dateUIFormat),
              effectiveDate: moment(period.start).format(this.dateUIFormat),
              expiredDate: expiredDate,
            };

            let minDate = moment(this.minDateFormat).format(this.dateUIFormat);
            //compare date
            if (moment(newPeriod.effectiveDate).isAfter(moment(minDate))) {
              this.minDateFormat = newPeriod.effectiveDate;
            }

            previousContractPeriods.push(newPeriod);
          });
        }
        this.contractPeriodsOptions = this.readonly
          ? [...previousContractPeriods]
          : [this.addNewContractPeriodOption, ...previousContractPeriods];
        this.sortContractPeriodOptionsByDate();
      });
  }

  sortContractPeriodOptionsByDate() {
    this.contractPeriodsOptions = this.contractPeriodsOptions.sort(
      (a, b) =>
        new Date(b.effectiveDate).getTime() -
        new Date(a.effectiveDate).getTime()
    );
  }

  addNewPeriodContractAction(isEnable: boolean) {
    if (!isEnable) {
      this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
        (option) => option.id != 0
      );
    } else {
      if (
        !(
          this.contractPeriodsOptions.filter((option) => option.id == 0)
            .length > 0
        )
      ) {
        this.contractPeriodsOptions = [
          this.addNewContractPeriodOption,
          ...this.contractPeriodsOptions,
        ];
      }
    }
  }

  onContractPeriodClear(event: any) {
    this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
      (option) => option.id != 0
    );
    this.addNewPeriodContractAction(true);
  }

  searchStates() {
    this.hospiceLOCRatesFormGroup.get("selectedCounties").enable();
    if (this.selectedStates.length === 0) {
      this.countyOptions = [];
    } else {
      let states = this.selectedStates.map((option) => option.value).join(",");
      this.payersService
        .getCountiesAndCBSAByState(states)
        .subscribe((response) => {
          if (response !== null) {
            this.mapCountiesResponse(response);
          }
        });
    }
    this.filterReadOnlyDataByStates();
  }

  filterReadOnlyDataByStates() {
    if (this.readonly) {
      this.cbsaRates = this.readOnlyCbsaRates;
      if (this.selectedStates && this.selectedStates.length > 0) {
        this.cbsaRates = this.readOnlyCbsaRates.filter((item) =>
          item.counties.some((countyGroup) =>
            countyGroup.some((county) =>
              this.selectedStates
                .map((option) => option.value)
                .includes(county["State"])
            )
          )
        );
      }
    }
  }

  mapCountiesResponse(countiesResponse) {
    this.countyOptions = [];
    countiesResponse.forEach((c) => {
      let county: Counties = {
        name: c.name,
        state: c.state,
        cbsa: c.cbsa,
        disabled: false,
        uniqueKey: c.name + "-" + c.state + "-" + c.cbsa,
      };
      this.countyOptions.push(county);
    });

    if (
      this.countyOptionsPreloaded.length > 0 &&
      this.stateOptionsPreloaded.length > 0
    ) {
      this.disableStatesAndCounties();
    }
  }

  removeCounty(county: Counties) {
    const selectedCounties =
      this.hospiceLOCRatesFormGroup.get("selectedCounties").value;
    let counties = this.countyOptions.filter((county) =>
      selectedCounties.includes(county.uniqueKey)
    );
    const updatedCounties = counties
      .filter(
        (selectedItem) =>
          selectedItem.name !== county.name ||
          selectedItem.cbsa !== county.cbsa ||
          selectedItem.state !== county.state
      )
      .map((item) => item.uniqueKey);
    this.hospiceLOCRatesFormGroup
      .get("selectedCounties")
      .setValue(updatedCounties);
    this.showCBSA();
  }

  removeState(state: Options) {
    const selectedStates = this.selectedStates;
    this.selectedStates = selectedStates.filter(
      (selectedItem) => selectedItem.name !== state.name
    );
    this.searchStates();
  }

  onClearAllStates() {
    this.selectedStates = [...this.stateOptionsPreloaded];
    if (this.selectedStates.length > 0) {
      this.searchStates();
    }
    this.onClearAllCounties();
  }

  onClearAllCounties() {
    this.hospiceLOCRatesFormGroup
      .get("selectedCounties")
      .setValue(this.countyOptionsPreloaded);
    if (
      this.countyOptionsPreloaded.length > 0 &&
      this.stateOptionsPreloaded.length > 0
    ) {
      this.disableStatesAndCounties();
    }
    this.showCBSA();
  }

  showCBSA() {
    if (this.readonly) {
      this.filterReadOnlyDataByCounties();
    } else {
      if (this.hospiceLOCRatesFormGroup.get("selectedContractPeriod").value) {
        this.addNewPeriodContractAction(true);
      }
      this.cbsaRatesPreviousSelected = this.cbsaRates;
      this.cbsaRates = [...this.cbsaRatesPreloaded];

      this.hospiceLOCRatesFormGroup
        .get("selectedCounties")
        .value.forEach((countyCode) => {
          let duplicate = false;
          const parts = countyCode.split("-");
          const cbsa = parts[2];
          let code: CBSAPayer = {
            cbsa: cbsa,
            displayDetails: false,
            isEditable: true,
            displayIcon: false,
          };
          this.cbsaRates.forEach((rate) => {
            if (rate.cbsa == cbsa) {
              duplicate = true;
            }
          });

          if (!duplicate) {
            this.cbsaRates.push(code);
            this.addCBSAFormControlRow(code.cbsa);
          }
        });

      if (this.cbsaRatesPreviousSelected.length > 0) {
        this.cbsaRatesPreviousSelected = this.cbsaRatesPreviousSelected.filter(
          (previous) =>
            !this.cbsaRates.some((rate) => rate.cbsa === previous.cbsa)
        );
      }

      this.cbsaRatesPreviousSelected.forEach((rate) => {
        this.removeCBSAFormControlRow(rate.cbsa);
      });

      this.cbsaRates.sort((a, b) => +a.cbsa - +b.cbsa);
    }
  }

  filterReadOnlyDataByCounties() {
    let selectedCounties =
      this.hospiceLOCRatesFormGroup.get("selectedCounties").value;
    this.filterReadOnlyDataByStates();
    if (selectedCounties && selectedCounties.length > 0) {
      let countyOptions = this.countyOptions.filter((county) =>
        selectedCounties.includes(county.uniqueKey)
      );
      let counties = new Set(countyOptions.map((item) => item.name));
      this.cbsaRates = this.cbsaRates.filter((item) =>
        item.counties.some((countyGroup) =>
          countyGroup.some((county) => counties.has(county["Name"]))
        )
      );
    }
  }

  allSelected(): boolean {
    return (
      this.hospiceLOCRatesFormGroup.get("selectedCounties").value &&
      this.hospiceLOCRatesFormGroup.get("selectedCounties").value.length ===
        this.countyOptions.length
    );
  }

  toggleSelectAll(): void {
    if (this.allSelected()) {
      this.hospiceLOCRatesFormGroup.get("selectedCounties").setValue([]);
      this.cbsaRates = [];
      this.cbsaRatesPreviousSelected = [];
    } else {
      this.hospiceLOCRatesFormGroup.get("selectedCounties").setValue([
        ...this.countyOptions.map((county) => {
          return county.uniqueKey;
        }),
      ]);
    }
    this.filterReadOnlyDataByCounties();
  }

  showSavePayerModal() {
    if (this.editMode) {
      let modalPage = this.modalCtrl.create(
        "SaveFacilityPage",
        {
          title: "Payer Information Change",
          commentPlaceholderKey: "archiveFacilityCommentPlaceHolder",
          popupMessageKey: "savePayerWarningMessage",
          section: "payer",
        },
        {
          enableBackdropDismiss: false,
          cssClass: "save-payer-confirmation-popup",
        }
      );
      modalPage.onDidDismiss((data) => {
        if (data.save) {
          this.saveLOCRates();
        }
      });
      modalPage.present();
    } else {
      this.saveLOCRates();
    }
  }

  saveLOCRates() {
    let spinnerText = "Saving Hospice LOC Rate";

    const spinner = this.loadingCtrl.create({
      content: spinnerText,
    });
    spinner.present();
    this.mapHospiceLOCRatesByContractPeriod(
      this.hospiceLOCRatesFormGroup.get("selectedContractPeriod").value
    );

    if (
      !this.hospiceLOCRatesData.payerId ||
      this.hospiceLOCRatesData.payerId.trim() === ""
    ) {
      this.getPayerTypeAndId();
      this.hospiceLOCRatesData.payerId = this.payerId;
      this.hospiceLOCRatesData.payerType = this.payerType;
    }

    this.payersService
      .postSavePayerRates(this.hospiceLOCRatesData)
      .pipe()
      .take(1)
      .subscribe(
        () => {
          spinner.dismiss();
          this.onHospiceLOCRateSaveSuccess.emit(true);
          this.onSaveSuccessAlert("Hospice LOC Rate Saved");
          this.saveEnabled = false;
          this.onRatesSaved.emit();

          // remove controls
          this.cbsaRates.forEach((rate) => {
            this.removeCBSAFormControlRow(rate.cbsa);
          });
          // clean data
          this.cleanData();
          this.hospiceLOCRatesFormGroup.reset();
          this.getPayerContractPeriods();
        },
        (error) => {
          spinner.dismiss();
          this.onSaveError(error);
        }
      );
  }

  checkCanSave() {
    this.saveEnabled =
      this.hospiceLOCRatesFormGroup &&
      this.hospiceLOCRatesFormGroup.valid &&
      this.periodSelected;
  }

  cancel() {
    this.onCanceled.emit(true);
  }

  set saveEnabled(value: boolean) {
    this._saveEnabled = value;
  }

  get saveEnabled() {
    return this._saveEnabled;
  }

  onContractPeriodChange(newValue: ContractPeriod) {
    if (!newValue) {
      this.cbsaRates.forEach((rate) => {
        this.removeCBSAFormControlRow(rate.cbsa);
      });
      this.cleanData();
      return;
    }

    this.previousContractPeriodSelected = newValue;
    if (newValue && newValue.id === 0) {
      if (newValue.value !== "Add New") {
        this.cleanData(false);
        this.periodSelected = true;
        return;
      }
      let contractPeriodModal = this.modalCtrl.create(
        "ContractPeriodModalComponent",
        { minDateFormat: this.minDateFormat },
        { cssClass: "contract-period-modal" }
      );
      contractPeriodModal.onDidDismiss((data) => {
        if (data) {
          this.cleanData();
          let newOption = this.formatContractPeriodModel(data);
          this.contractPeriodsOptions = this.contractPeriodsOptions.filter(
            (dateOption) => dateOption.id !== 0
          );
          this.contractPeriodsOptions = [
            ...this.contractPeriodsOptions,
            newOption,
          ];
          this.sortContractPeriodOptionsByDate();

          this.hospiceLOCRatesFormGroup.reset();
          this.hospiceLOCRatesFormGroup
            .get("selectedContractPeriod")
            .setValue(newOption);
          this.periodSelected = true;
          this.hospiceLOCRatesFormGroup.get("selectedCounties").enable();
        } else {
          if (this.previousContractPeriodSelected.id == 0) {
            this.periodSelected = false;
            this.hospiceLOCRatesFormGroup.get("selectedCounties").disable();
            this.hospiceLOCRatesFormGroup
              .get("selectedContractPeriod")
              .setValue(null);
          } else {
            this.periodSelected = true;
            this.hospiceLOCRatesFormGroup.get("selectedCounties").enable();
            this.hospiceLOCRatesFormGroup
              .get("selectedContractPeriod")
              .setValue(this.previousContractPeriodSelected);
          }
        }
      });

      contractPeriodModal.present();
    } else if (newValue && newValue.id !== 0) {
      let spinnerText = "Loading Hospice LOC rates";

      const spinner = this.loadingCtrl.create({
        content: spinnerText,
      });
      spinner.present();

      let effectiveDate = moment(
        this.hospiceLOCRatesFormGroup.get("selectedContractPeriod").value
          .effectiveDate
      ).format(this.dateFormat);

      let payerId = this.payerId;
      let benefitType = this.benefitType;
      let benefitSubtype = this.benefitSubtype;

      if (this.readonly) {
        payerId = "MEDICARE";
        benefitType = "HOSPICE";
        benefitSubtype = "LOC";
      }

      this.payersService
        .getPayerRates(payerId, benefitType, effectiveDate, benefitSubtype)
        .pipe()
        .take(1)
        .subscribe(
          (response) => {
            spinner.dismiss();
            if (response !== null) {
              this.editMode = true;
              this.mapHospiceLOCRatesIntoForm(response);
              this.enableFilters();
            }
            this.periodSelected = true;
          },
          (error) => {
            spinner.dismiss();
            this.onSaveError(error);
          }
        );
    } else {
      this.periodSelected = false;
      this.hospiceLOCRatesFormGroup.get("selectedCounties").disable();
    }
  }

  enableFilters() {
    if (this.readonly) {
      this.readOnlyCbsaRates = this.cbsaRates;
      this.periodSelected = true;
      this.hospiceLOCRatesFormGroup.get("selectedCounties").enable();
    }
  }

  formatContractPeriodModel(period: any): ContractPeriod {
    let newContractPeriod = moment(new Date(period)).format(this.dateUIFormat);
    let newEffectiveDate = moment(new Date(period)).format(this.dateUIFormat);
    return {
      id: 0,
      name: newContractPeriod,
      value: newContractPeriod,
      effectiveDate: newEffectiveDate,
      expiredDate: null,
    };
  }

  mapHospiceLOCRatesIntoForm(rates) {
    this.cleanData();
    this.hospiceLOCRatesFormGroup.get("selectedCounties").enable();
    rates.rates.forEach((rate) => {
      let countiesDetails = this.chunkCountiesArray(rate.Counties);
      this.cbsaRatesPreloaded.push({
        cbsa: rate.Cbsa,
        displayDetails: false,
        counties: countiesDetails,
        isEditable: !this.readonly,
        displayIcon: false,
      });
      this.addCBSAFormControlRow(rate.Cbsa);
      if (!this.readonly) {
        this.mapPreloadedStates(rate.Counties);
      }
      this.hospiceLOCRatesFormGroup
        .get("rc61-" + rate.Cbsa)
        .setValue(this.addDecimals(rate.RoutineHomeCareLow));
      this.hospiceLOCRatesFormGroup
        .get("rc60-" + rate.Cbsa)
        .setValue(this.addDecimals(rate.RoutineHomeCareHigh));
      this.hospiceLOCRatesFormGroup
        .get("continuousCare-" + rate.Cbsa)
        .setValue(this.addDecimals(rate.ContinuousCare));
      this.hospiceLOCRatesFormGroup
        .get("inpatientRC-" + rate.Cbsa)
        .setValue(this.addDecimals(rate.RespiteCare));
      this.hospiceLOCRatesFormGroup
        .get("generalI-" + rate.Cbsa)
        .setValue(this.addDecimals(rate.GeneralInpatient));
    });

    this.cbsaRates = [...this.cbsaRatesPreloaded];
  }

  addDecimals(value: number): string {
    if (value % 1 !== 0) {
      return value.toString();
    }
    return value.toFixed(2);
  }

  chunkCountiesArray(counties: Counties[]): string[][] {
    const chunkSize = Math.ceil(counties.length / this.chunkCountiesSize);
    const result = [];
    for (let i = 0; i < counties.length; i += chunkSize) {
      result.push(counties.slice(i, i + chunkSize));
    }
    return result;
  }

  mapPreloadedStates(states) {
    let duplicate = false;
    states.forEach((preload) => {
      this.countyOptionsPreloaded.forEach((state) => {
        if (state == preload.uniqueKey) {
          duplicate = true;
        }
      });

      if (!duplicate) {
        this.countyOptionsPreloaded.push(
          preload.Name + "-" + preload.State + "-" + preload.Cbsa
        );
      }

      duplicate = false;

      this.stateOptionsPreloaded.forEach((state) => {
        if (state.name === preload.State) {
          duplicate = true;
        }
      });

      if (!duplicate) {
        let opt: Options = {
          name: preload.State,
          value: preload.State,
          disabled: true,
        };
        this.stateOptionsPreloaded.push(opt);
      }
      duplicate = false;
    });

    this.selectedStates = [...this.stateOptionsPreloaded];

    this.searchStates();

    this.hospiceLOCRatesFormGroup
      .get("selectedCounties")
      .setValue(this.countyOptionsPreloaded);
  }

  disableStatesAndCounties() {
    this.stateOptions = this.stateOptions.map((option) => {
      if (
        this.stateOptionsPreloaded
          .map((option) => option.value)
          .includes(option.name)
      ) {
        return { ...option, disabled: true };
      }
      return option;
    });

    this.countyOptions = this.countyOptions.map((countyOpt) => {
      if (
        this.countyOptionsPreloaded.some(
          (county) => countyOpt.uniqueKey === county
        )
      ) {
        return { ...countyOpt, disabled: true };
      }
      return countyOpt;
    });
  }

  addCBSAFormControlRow(cbsa: string) {
    this.hospiceLOCRatesFormGroup.addControl(
      "rc60-" + cbsa,
      new FormControl(null, [Validators.required])
    );
    this.hospiceLOCRatesFormGroup.addControl(
      "rc61-" + cbsa,
      new FormControl(null, [Validators.required])
    );
    this.hospiceLOCRatesFormGroup.addControl(
      "continuousCare-" + cbsa,
      new FormControl(null, [Validators.required])
    );
    this.hospiceLOCRatesFormGroup.addControl(
      "inpatientRC-" + cbsa,
      new FormControl(null, [Validators.required])
    );
    this.hospiceLOCRatesFormGroup.addControl(
      "generalI-" + cbsa,
      new FormControl(null, [Validators.required])
    );
  }

  removeCBSAFormControlRow(cbsa: string) {
    this.hospiceLOCRatesFormGroup.removeControl("rc60-" + cbsa);
    this.hospiceLOCRatesFormGroup.removeControl("rc61-" + cbsa);
    this.hospiceLOCRatesFormGroup.removeControl("continuousCare-" + cbsa);
    this.hospiceLOCRatesFormGroup.removeControl("inpatientRC-" + cbsa);
    this.hospiceLOCRatesFormGroup.removeControl("generalI-" + cbsa);
  }

  mapHospiceLOCRatesByContractPeriod(period: ContractPeriod) {
    let hospiceLOCRate: HospiceLOCPayerRates;

    let effectiveDate = moment(period.effectiveDate).format(this.dateFormat);
    let expiredDate;
    if (period.expiredDate != null) {
      expiredDate = moment(period.expiredDate).format(this.dateFormat);
    }

    hospiceLOCRate = {
      benefitSubtype: "LOC",
      benefitType: "HOSPICE",
      payerId: this.payerId,
      payerType: this.payerType,
      rates: [],
      useDefaultCodes: false,
      start: effectiveDate,
      end: expiredDate,
    };

    this.cbsaRates.forEach((rate) => {
      let newRate: HospiceLOCRate = {
        cbsa: +rate.cbsa,
        continuousCare: +this.hospiceLOCRatesFormGroup.get(
          "continuousCare-" + rate.cbsa
        ).value,
        generalInpatient: +this.hospiceLOCRatesFormGroup.get(
          "generalI-" + rate.cbsa
        ).value,
        respiteCare: +this.hospiceLOCRatesFormGroup.get(
          "inpatientRC-" + rate.cbsa
        ).value,
        routineHomeCareHigh: +this.hospiceLOCRatesFormGroup.get(
          "rc60-" + rate.cbsa
        ).value,
        routineHomeCareLow: +this.hospiceLOCRatesFormGroup.get(
          "rc61-" + rate.cbsa
        ).value,
        sia: +(
          this.hospiceLOCRatesFormGroup.get("continuousCare-" + rate.cbsa)
            .value / 96
        ).toFixed(2),
      };

      hospiceLOCRate.rates.push(newRate);
    });

    this.hospiceLOCRatesData = hospiceLOCRate;
  }

  private onSaveSuccessAlert = (message: string) => {
    this.showAlert({
      title: "Success",
      message: message,
    });
  };

  showAlert = ({ title, message }) => {
    let alert = this.alertCtrl.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
          handler: () => {},
        },
      ],
    });
    alert.present();
  };

  private onSaveError = (error) => {
    if (error instanceof HttpErrorResponse) {
      if (error.error) {
        this.showAlert({
          title: "Error",
          message: error.error.message,
        });
      } else {
        this.showAlert({
          title: "Error",
          message: error.message,
        });
      }
    } else {
      this.showAlert({ title: "Error", message: error });
    }
  };

  toggleDisplayDetails(cbsa) {
    cbsa.displayDetails = !cbsa.displayDetails;
    if (!cbsa.counties || cbsa.counties.length == 0) {
      let spinnerText = "Loading...";

      const spinner = this.loadingCtrl.create({
        content: spinnerText,
      });
      spinner.present();
      this.payersService
        .getCountiesByCBSA(cbsa.cbsa)
        .pipe()
        .take(1)
        .subscribe(
          (response) => {
            spinner.dismiss();
            if (response !== null) {
              cbsa.counties = this.chunkCountiesArray(Object.values(response));
            }
          },
          () => {
            spinner.dismiss();
          }
        );
    }
  }

  showCopyIcon(cbsa, controlName) {
    this.rc60CopyOption = false;
    this.rc61CopyOption = false;
    this.continuousCareCopyOption = false;
    this.inpatientRCCopyOption = false;
    this.generalICopyOption = false;
    if (controlName == "rc60") this.rc60CopyOption = true;
    else if (controlName == "rc61") this.rc61CopyOption = true;
    else if (controlName == "continuousCare")
      this.continuousCareCopyOption = true;
    else if (controlName == "inpatientRC") this.inpatientRCCopyOption = true;
    else if (controlName == "generalI") this.generalICopyOption = true;

    this.cbsaRates.forEach((rate) => {
      rate.displayIcon = false;
    });

    const lastElement = this.cbsaRates[this.cbsaRates.length - 1];
    if (cbsa.cbsa == lastElement.cbsa) {
      cbsa.displayIcon = false;
    } else {
      cbsa.displayIcon = true;
    }
  }

  hideIcon(event) {
    if (event.relatedTarget == null) {
      this.cbsaRates.forEach((rate) => {
        rate.displayIcon = false;
      });
    }
  }

  setCopiedValue(cbsa, controlName) {
    let currentControlValue = this.hospiceLOCRatesFormGroup.get(
      controlName + "-" + cbsa.cbsa
    ).value;

    if (currentControlValue) {
      this.cbsaRates.forEach((rate) => {
        if (rate.cbsa > cbsa.cbsa)
          this.hospiceLOCRatesFormGroup
            .get(controlName + "-" + rate.cbsa)
            .setValue(currentControlValue);
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/rates-payer/hospice-loc/hospice-loc.ts